import type { ModuleReferenceInfo } from './module-reference'
import { ModuleTypeEnum } from './module'

export enum DocumentTypeEnum {
  FILE = 'FILE',
  URL = 'URL',
}

export type DocumentMimeType =
  | 'IMAGE'
  | 'DOCUMENT'
  | 'VIDEO'
  | 'AUDIO'
  | 'OTHER'

/**
 * Interface for link preview API response
 */
export interface LinkPreviewResponse {
  url: string
  title?: string
  siteName?: string
  description?: string
  mediaType?: string
  contentType?: string
  images?: string[]
  videos?: {
    url: string
    secureUrl: string
    type: string
    width: string
    height: string
  }[]
  favicons?: string[]
}

/**
 * Type alias for PreviewData, using UrlPreviewData
 */
export type PreviewData = UrlPreviewData

/**
 * Metadata for document storage
 */
export interface DocumentMetadata {
  author?: string
  createdDate?: string
  modifiedDate?: string
  pageCount?: number
  wordCount?: number
  thumbnailGenerated?: boolean
  originalFilename?: string
  [key: string]: unknown
}

export interface UrlPreviewData {
  title?: string | unknown
  description?: string | unknown
  image?: string | unknown
  siteName?: string | unknown
  url: string | unknown
  [key: string]: unknown
}

/**
 * Converts a raw link preview response to our standardized UrlPreviewData format
 */
export function convertToUrlPreviewData(
  response: LinkPreviewResponse,
): UrlPreviewData {
  return {
    title: response.title || null,
    description: response.description || null,
    image: response.images?.[0] || null,
    siteName: response.siteName || null,
    url: response.url,
  }
}

export interface Document {
  id: string
  organizationId: string
  categoryId?: string
  type: DocumentTypeEnum
  displayName: string
  description?: string

  // File-specific fields
  fileName?: string
  contentType?: string
  fileSize?: number
  storageKey?: string
  thumbnailKey?: string

  // URL-specific fields
  url?: string
  urlPreviewData?: UrlPreviewData
  lastUrlCheck?: Date
  isUrlValid?: boolean

  // Common fields
  uploadedBy?: string
  moduleType: string
  moduleId: string
  order: number
  isPinned: boolean
  isArchived: boolean
  isProtected: boolean
  metadata?: Record<string, unknown>
  tags: string[]
  documentDate?: Date
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

// Internal type used by the service layer that ensures date fields are Date objects
export type InternalDocument = Omit<Document, 'metadata'> & {
  metadata?: Record<string, unknown> // Use Record<string, unknown> for JSON fields
  documentDate?: Date
}

export interface DocumentCategory {
  id: string
  organizationId: string
  moduleType?: string
  moduleId?: string
  parentId?: string
  name: string
  description?: string
  icon?: string
  color?: string
  order: number
  isProtected: boolean
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export interface DocumentAttachment {
  id: string
  documentId: string
  organizationId: string
  moduleType: string
  moduleId: string
  attachedBy?: string
  createdAt: Date
}

export interface DocumentAttachmentResponse {
  id: string
  documentId: string
  moduleType: string
  moduleId: string
  attachedBy?: string
  createdAt: string
  document?: DocumentResponse
}

export interface AttachDocumentsDTO {
  documentIds: string[]
  moduleType: string
  moduleId: string
}

export interface DetachDocumentDTO {
  documentId: string
  moduleType: string
  moduleId: string
}

export interface CreateDocumentDTO {
  file?: File | Blob
  url?: string
  displayName: string
  description?: string
  moduleType: ModuleTypeEnum
  moduleId: string
  organizationId: string
  categoryId?: string
  tags?: string[]
  metadata?: Record<string, unknown>
  documentDate?: Date
}

export interface DocumentResponse {
  id: string
  displayName: string
  type: DocumentTypeEnum
  storageKey?: string
  thumbnailKey?: string
  url?: string
  fileName?: string
  contentType?: string
  fileSize?: number
  metadata?: Record<string, unknown>
  tags: string[]
  createdAt: string
  isPinned: boolean
  moduleType: string
  moduleId: string
  categoryId?: string
  documentDate?: string
  moduleInfo?: ModuleReferenceInfo | null
  uploadedBy?: string
  isProtected?: boolean
  customFields?: Record<string, unknown>
  [key: `custom_${string}`]: unknown
}

// =====================
// Document protection (access-restricted folders & files)
// =====================

export interface DocumentAccessMemberUser {
  id: string
  firstName?: string | null
  lastName?: string | null
  displayName?: string | null
  email?: string
  image?: string | null
}

export interface DocumentAccessMemberResponse {
  id: string
  userId: string
  user?: DocumentAccessMemberUser
  addedBy?: string
  createdAt: string
}

export interface DocumentProtectionResponse {
  isProtected: boolean
  members: DocumentAccessMemberResponse[]
  // Set when a protected ancestor folder also restricts this item
  inheritedFrom?: {
    categoryId: string
    name: string
  }
}

export interface UpdateDocumentProtectionDTO {
  isProtected: boolean
  userIds?: string[]
}

export interface UpdateDocumentAccessListDTO {
  userIds: string[]
}

// Constants for file size limits
export const FILE_SIZE_LIMITS: Record<DocumentMimeType, number> = {
  IMAGE: 10 * 1024 * 1024, // 10MB
  DOCUMENT: 50 * 1024 * 1024, // 50MB
  VIDEO: 100 * 1024 * 1024, // 100MB
  AUDIO: 50 * 1024 * 1024, // 50MB
  OTHER: 10 * 1024 * 1024, // 10MB
} as const

// Constants for allowed mime types
export const ALLOWED_MIME_TYPES: Record<
  Exclude<DocumentMimeType, 'OTHER'>,
  readonly string[]
> = {
  IMAGE: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'image/tiff',
    'image/bmp',
  ],
  DOCUMENT: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/postscript',
    'application/illustrator',
    'application/x-illustrator',
    'application/rtf',
    'application/zip',
    'text/plain',
    'text/csv',
    'text/html',
    'text/javascript',
    'text/css',
    'application/json',
    'application/xml',
    'application/octet-stream',
  ],
  VIDEO: [
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/mpeg',
    'video/webm',
    'video/ogg',
    'video/3gpp',
  ],
  AUDIO: [
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/mp3',
    'audio/mp4',
    'audio/aac',
    'audio/webm',
  ],
} as const

// Thumbnail sizes
export const THUMBNAIL_SIZES = {
  SMALL: { width: 150, height: 150 },
  MEDIUM: { width: 300, height: 300 },
  LARGE: { width: 600, height: 600 },
} as const

// Helper function to determine document type from mime type
export function getDocumentType(mimeType: string): DocumentMimeType {
  if (mimeType.startsWith('image/')) {
    return 'IMAGE'
  } else if (mimeType.startsWith('video/')) {
    return 'VIDEO'
  } else if (mimeType.startsWith('audio/')) {
    return 'AUDIO'
  } else if (
    mimeType.startsWith('application/') ||
    mimeType.startsWith('text/')
  ) {
    return 'DOCUMENT'
  }
  return 'OTHER'
}

// Flag to control mime type enforcement (set to false to allow all types)
export const ENFORCE_MIME_TYPE_RESTRICTIONS = false
